import { useEffect, useState, type ReactNode } from 'react'
import { signedInRoleCodes, signersText } from '../../lib/api'
import type { ExitChain, ExitDecisionResult, ExitStageState } from '../../lib/exit'
import { personLabel, usePeopleNames } from '../../lib/people'
import { useSave } from '../../lib/save'
import { ErrorBox } from '../DataTable'
import { signFooter } from '../footers'
import { Dialog, StageList, Status, Toggletip, type ListStage, type StageState } from '../kit'
import { AboutStageDrawer } from './AboutStageDrawer'
import { useKept } from './hooks'
import { dayMonth, same, stageHelp, stagePosition, stamp, truncate } from './words'

// The sign-off chain as a list: one line per stage, who signed and when, the
// one thing holding the current stage, and the Sign and Decline actions for a
// person who may take them.
//
// # WHICH CONTROLS ARE GREY, AND WHY THE SCREEN KNOWS
//
// Nothing here decides who may sign. The walls the API applies are read in
// the same order from the chain it returned: a role the stage names, not the
// person who started the exit, and not somebody who already decided another
// stage. They grey the button with a short reason before the person presses
// it; the API refuses in its own words if the screen is ever wrong.

// Why this person cannot sign this stage, in a few words, or '' when they can.
export function signRefusal(chain: ExitChain, stage: ExitStageState, subject: string): string {
  const mine = signedInRoleCodes()
  const holdsRole = mine.length === 0 || stage.roles.some((r) => mine.some((m) => same(m, r)))
  if (!holdsRole) return 'Signed by ' + signersText(stage.roles, stage.roleNames)
  if (subject !== '' && chain.maker !== undefined && same(chain.maker, subject)) return 'You started this offboarding, so others sign it'
  const decided = chain.stages.find((s) => s.state !== 'outstanding' && subject !== '' && same(s.signedBy ?? '', subject))
  if (decided && decided.key !== stage.key) return 'You already decided the ' + decided.job + ' stage'
  return ''
}

// Whether the stage is the one open to a decision right now.
export function decidable(stage: ExitStageState, signable: boolean, subject: string): boolean {
  return signable && stage.next && (stage.state === 'outstanding' || (stage.state === 'declined' && same(stage.signedBy ?? '', subject)))
}

// The states the track and the list draw for a vendor's chain.
export function chainStates(chain: ExitChain, underWay: boolean, blocked: boolean): StageState[] {
  const at = stagePosition(chain.stages)
  return chain.stages.map((s, i) => {
    if (s.state === 'signed') return 'done'
    if (s.state === 'declined') return 'rejected'
    if (underWay && i === at) return blocked ? 'blocked' : 'current'
    return 'upcoming'
  })
}

export type Signing = { stage: ExitStageState; decision: 'SIGN' | 'DECLINE' }

export function ExitChainPanel({
  chain,
  underWay,
  signable,
  subject,
  blocker,
  blockerItemKey,
  dueOn,
  mayRecord,
  accounts,
  onSign,
  onRecordItem,
}: {
  chain: ExitChain
  underWay: boolean
  signable: boolean
  subject: string
  blocker?: string
  blockerItemKey?: string
  dueOn?: string
  mayRecord: boolean
  // What the customer's directory showed for the vendor's accounts, and the
  // stage that vouches for them, so that stage says "n of m accounts closed".
  accounts?: { stageKey: string; closed: number; total: number; met: boolean; itemKey: string }
  onSign: (s: Signing) => void
  onRecordItem: (key: string) => void
}) {
  const [about, setAbout] = useState(false)
  const names = usePeopleNames()
  const label = (s: string | undefined) => personLabel(names, s)

  if (chain.total === 0) {
    return (
      <section className="panel exit-panel">
        <h2>Sign-off chain</h2>
        <p className="exit-sub">No offboarding chain applies at this tier; the approval matrix needs an offboarding row</p>
      </section>
    )
  }

  const states = chainStates(chain, underWay, !!blocker && !blocker.startsWith('Declined'))
  const at = stagePosition(chain.stages)

  const list: ListStage[] = chain.stages.map((s, i) => {
    const state = states[i] ?? 'upcoming'
    const decided = s.state !== 'outstanding'
    const isAt = underWay && i === at
    let blockerRow: ReactNode
    if (isAt && s.state === 'declined') {
      blockerRow = (
        <>
          <strong>Declined</strong>
          <span title={s.note}>{truncate(s.note ?? '', 64)}</span>
        </>
      )
    } else if (isAt && blocker) {
      blockerRow = (
        <>
          <strong>Blocked by</strong> {blocker}
          <span className="exit-grow" />
          {mayRecord && blockerItemKey && (
            <button type="button" className="exit-link" onClick={() => onRecordItem(blockerItemKey)}>
              Record it
            </button>
          )}
        </>
      )
    } else if (s.superseded) {
      blockerRow = (
        <>
          <strong>Needs signing again</strong>
          <Toggletip label={'Why the ' + s.job + ' stage needs signing again'}>{truncate(s.superseded, 200)}</Toggletip>
        </>
      )
    }

    let detail: ReactNode
    // The refusal already names who signs, so the line under the stage does
    // not say it a second time.
    const refusal = decidable(s, signable, subject) ? signRefusal(chain, s, subject) : ''
    const proof =
      accounts && accounts.stageKey === s.key ? (
        <button type="button" className="exit-link" onClick={() => onRecordItem(accounts.itemKey)}>
          <Status tone={accounts.met ? 'ok' : 'warn'}>
            {accounts.closed} of {accounts.total} accounts closed
          </Status>
        </button>
      ) : undefined
    if (decidable(s, signable, subject)) {
      detail = (
        <span className="exit-sign">
          <button type="button" className="primary" disabled={refusal !== ''} onClick={() => onSign({ stage: s, decision: 'SIGN' })}>
            Sign
          </button>
          {s.state === 'outstanding' && (
            <button type="button" disabled={refusal !== ''} onClick={() => onSign({ stage: s, decision: 'DECLINE' })}>
              Decline
            </button>
          )}
          {refusal && <span className="exit-sub">{refusal}</span>}
        </span>
      )
    }
    if (proof) {
      detail = (
        <>
          {proof}
          {detail}
        </>
      )
    }

    return {
      key: s.key,
      label: s.job,
      state,
      person: decided ? label(s.signedBy) : undefined,
      sub: decided || refusal.startsWith('Signed by') ? undefined : signersText(s.roles, s.roleNames),
      when: decided ? stamp(s.signedAt) : isAt && dueOn ? 'due ' + dayMonth(dueOn) : undefined,
      tip: truncate(s.why, 200),
      blocker: blockerRow,
      detail,
    }
  })

  return (
    <section className="panel exit-panel" aria-labelledby="exit-chain-h">
      <div className="exit-panel-head">
        <h2 id="exit-chain-h" className="exit-grow">
          Sign-off chain
        </h2>
        <span className="exit-sub">
          {chain.signed} of {chain.total} signed
        </span>
        <button type="button" className="exit-link" onClick={() => setAbout(true)}>
          About this chain
        </button>
      </div>
      <StageList stages={list} />
      <AboutStageDrawer chain={chain} open={about} onClose={() => setAbout(false)} />
    </section>
  )
}

// Signing or declining one stage, with the note kept beside the decision.
export function SignDrawer({
  vendorId,
  signing: current,
  onClose,
  onDecided,
}: {
  vendorId: string
  signing: Signing | null
  onClose: () => void
  onDecided: (r: ExitDecisionResult) => void
}) {
  const signing = useKept(current)
  const [note, setNote] = useState('')
  const [err, setErr] = useState<unknown>(null)

  useEffect(() => {
    if (!current) return
    setNote('')
    setErr(null)
  }, [current])

  const decline = signing?.decision === 'DECLINE'
  const title = signing ? (decline ? 'Decline the ' : 'Sign the ') + signing.stage.job + ' stage' : ''
  const save = useSave(current ? current.stage.key + ' ' + current.decision : null, title)



  return (
    <Dialog
      shape="modal"
      kind="form"
      open={current !== null}
      onClose={onClose}
      title={title}
      {...(signing
        ? signFooter({ vendorId, stageKey: signing.stage.key, decision: signing.decision, note, busy: save.busy }, (work) => {
            setErr(null)
            save.send(work, { done: onDecided, failed: setErr })
          })
        : {})}
    >
      {signing && (
        <div className="exit-drawer-stack">
          <p className="exit-para">
            {signing.stage.job} {stageHelp(signing.stage.key)}
          </p>
          {err != null && <ErrorBox error={err} what="That decision" verb="recorded" />}
          <div className="field">
            <label htmlFor="exit-sign-note">{decline ? 'What would have to change' : 'Note, optional'}</label>
            <textarea id="exit-sign-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
            <p className="field-note">Kept with the decision on the evidence chain</p>
          </div>
          <details className="exit-why">
            <summary>Why this stage exists</summary>
            <p className="exit-para">{signing.stage.why}</p>
          </details>
        </div>
      )}
    </Dialog>
  )
}
